/**
 * x-form directive.
 *
 * Manages form state, client-side validation, field population, and submission
 * lifecycle. Reads the validation schema and initial response/form data from an
 * inline `<script type="application/json">` element inside the form.
 *
 * Validation is performed via the `$validator` magic against the JSON schema.
 * Error state is reflected on each `.form-control` element using `aria-invalid`
 * and an associated feedback element. The current status is reflected as a
 * `data-status` attribute and CSS class on the form element.
 *
 * Adds a `.novalidate` escape hatch: any form group nested inside an element
 * with this class is excluded from validation.
 *
 * @options
 *   use_form_data      - Pass `FormData` to the validator instead of a plain
 *                        object (default: false).
 *   validate_on_submit - Run full validation on submit; prevent native submission
 *                        if there are errors (default: false).
 *
 * @events dispatched
 *   status-changed - Fired whenever the form status changes with `{ form, status, previousStatus }`.
 *   submitting     - Fired on valid submission with `{ submitEvent }`.
 *
 * @example
 *   <form x-form="{ validate_on_submit: true }">
 *     <script type="application/json">{ "schema": {…} }</script>
 *     <div class="form-control-group" data-name="email" data-id="Email">
 *       <input class="form-control" name="email" type="email" required>
 *     </div>
 *   </form>
 */
(() => {
  /** @type {string} CSS selector for the no-validate escape-hatch class. */
  const NOVALIDATE_CLASS = ".novalidate";
  /** @type {string} CSS selector for individual form control elements. */
  const FORM_CONTROL_CLASS = ".form-control";
  /** @type {string} CSS selector for form control group wrapper elements. */
  const FORM_CONTROL_GROUP_CLASS = ".form-control-group";

  /**
   * Map of internal status keys to their public CSS class names.
   * Custom status values not found in this map are prefixed with `custom-`.
   * @type {Map<string, string>}
   */
  const DEFAULT_STATUSES = new Map([
    ["init", "init"],
    ["validation_failed", "invalid"],
    ["aborted", "aborted"],
    ["submitting", "submitting"],
    ["validating", "validating"],
  ]);

  const root = (
    Alpine,
    el,
    { use_form_data = false, validate_on_submit = false },
  ) => {
    const {
      schema,
      response = {},
      formData = {},
    } = JSON.parse(
      el.querySelector('script[type="application/json"]:first-of-type')
        .textContent,
    );

    Alpine.bind(el, () => ({
      ["@submit"]($event) {
        this.handleSubmit($event);
      },

      ["@change"]($event) {
        this.handleChange($event);
      },

      [":data-status"]() {
        return this.status;
      },

      [":class"]() {
        return {
          [this.status]: true,
        };
      },

      ["x-data"]() {
        return {
          submitted: false,
          blocked: false,
          response: response,
          __errors: [],
          __status: null,

          /**
           * Sets the initial form status, applies any server-side validation
           * errors from the response, and populates field values from `formData`.
           */
          init() {
            this.$watch("__status", (value, oldValue) => {
              this.$dispatch("status-changed", {
                form: this,
                status: value,
                previousStatus: oldValue,
              });
            });

            this.status = "init";

            if (this.response.invalid_fields) {
              this.response.invalid_fields.forEach(({ field, message }) => {
                this.setValidationError(field, message);
              });
            }

            if (formData && Object.keys(formData).length > 0) {
              this.populateForm(formData);
            }
          },

          /**
           * Handles form submission. When `validate_on_submit` is enabled,
           * validates the form before submission and blocks it if there are errors.
           * Dispatches the `submitting` event to allow AJAX handling.
           * @param {SubmitEvent} $event - The native submit event.
           * @returns {Promise<void>}
           */
          async handleSubmit($event) {
            if (this.blocked) {
              $event.preventDefault();
              $event.stopImmediatePropagation();

              return;
            }

            if (validate_on_submit) {
              await this.validateForm({
                scope: this.$el,
              });

              if (this.hasErrors) {
                $event.preventDefault();
                $event.stopImmediatePropagation();

                this.focusFirstInvalidField();
                return;
              }
            }

            this.blocked = true;
            this.status = "submitting";

            this.clearResponse();
            this.announceToScreenReader("Submitting form, please wait...");

            // Dispatch to allow AJAX handling. Use new FormData(this.$el) to
            // access the submitted form data in the handler.
            this.$dispatch("submitting", {
              submitEvent: $event,
            });
          },

          /**
           * Validates the changed field when a change event occurs inside a
           * form control group.
           * @param {Event} $event - The native change event.
           */
          handleChange($event) {
            if ($event.target.closest(FORM_CONTROL_GROUP_CLASS)) {
              this.validateField($event.target, {
                scope: this.$el,
                target: $event.target,
              });
            }
          },

          /**
           * Unblocks the form after an AJAX response. Sets `submitted` to true
           * on success.
           * @param {boolean} success - Whether the server returned a success response.
           * @returns {Promise<void>}
           */
          async handleResponse(success) {
            this.blocked = false;

            if (success) {
              this.submitted = true;
            }
          },

          /**
           * Resets the form to its initial state, clearing any response messages
           * and the submitted flag.
           */
          handleReset() {
            this.clearResponse();
            this.status = "init";
            this.submitted = false;
          },

          /**
           * Programmatically populates form field values from a plain object.
           * Supports text, checkbox, radio, select-multiple, and file inputs.
           * File inputs are skipped for security reasons.
           * @param {Record<string, string|string[]>} data - Key-value map of
           *   field names to values.
           */
          populateForm(data) {
            Object.entries(data).forEach(([fieldName, value]) => {
              const formGroup = this.$el.querySelector(
                `${FORM_CONTROL_GROUP_CLASS}[data-name="${fieldName}"]`,
              );

              if (!formGroup) {
                return;
              }

              const controls = formGroup.querySelectorAll(
                `:where(input, textarea, select)[name="${fieldName}"]:enabled`,
              );

              controls.forEach((control) => {
                switch (control.type) {
                  case "checkbox":
                    if (Array.isArray(value)) {
                      control.checked = value.includes(control.value);
                    } else {
                      control.checked = Boolean(value);
                    }
                    break;
                  case "radio":
                    control.checked = control.value === String(value);
                    break;
                  case "select-multiple":
                    if (Array.isArray(value)) {
                      Array.from(control.options).forEach((option) => {
                        option.selected = value.includes(option.value);
                      });
                    }
                    break;
                  case "file":
                    // File inputs cannot be programmatically set for security reasons.
                    break;
                  default:
                    control.value = value;
                }
              });
            });
          },

          /**
           * Clears the server response message and invalid fields list.
           */
          clearResponse() {
            this.response.message = "";
            this.response.invalid_fields = [];
          },

          /**
           * Returns true when the given target element should be skipped during
           * validation (i.e. it is outside the form, not in a control group,
           * or inside a `.novalidate` element).
           * @param {Element} target - The form control element to check.
           * @returns {boolean} True when validation should be skipped.
           */
          skipValidation(target) {
            if (!this.$el.contains(target)) {
              return true;
            }

            // Event target is not a form control.
            if (!target.closest(FORM_CONTROL_GROUP_CLASS)) {
              return true;
            }

            if (target.closest(NOVALIDATE_CLASS)) {
              return true;
            }

            return false;
          },

          /**
           * Validates all enabled form fields against the JSON schema.
           * Sets or clears validation errors on each form control group.
           * @param {object} [options={}] - Options passed to `$validator.validate`.
           * @returns {Promise<void>}
           */
          async validateForm(options = {}) {
            options.useFormData = use_form_data;

            this.clearErrors();

            const formGroups = this.$el.querySelectorAll(
              FORM_CONTROL_GROUP_CLASS,
            );

            const formData = this.getFormData();

            const prevStatus = this.status;

            this.status = "validating";

            return Promise.resolve(this.status)
              .then(() => {
                const result = this.$validator.validate(
                  schema,
                  formData,
                  options,
                );

                for (const formGroup of formGroups) {
                  if (undefined === formGroup.dataset.name) {
                    continue;
                  }

                  const fieldName = formGroup.dataset.name;

                  if (result.has(fieldName)) {
                    const { error } = result.get(fieldName);

                    this.removeValidationError(fieldName);

                    const input = formGroup.querySelector(
                      `:where([name="${fieldName}"]):enabled`,
                    );

                    if (undefined !== error && input) {
                      this.setValidationError(fieldName, error, options);
                    }
                  }
                }
              })
              .finally(() => {
                this.status = prevStatus;
              });
          },

          /**
           * Validates all fields in the form up to and including the target
           * field. Stops processing further groups once the target's group is
           * reached, so only previously seen fields accumulate errors.
           * @param {Element} target - The form control that triggered the change.
           * @param {object} [options={}] - Options passed to `$validator.validate`.
           */
          validateField(target, options = {}) {
            if (undefined !== target && this.skipValidation(target)) {
              return;
            }

            options.useFormData = use_form_data;

            this.clearErrors();

            const formGroups = this.$el.querySelectorAll(
              FORM_CONTROL_GROUP_CLASS,
            );

            const formData = this.getFormData();

            const prevStatus = this.status;

            this.status = "validating";

            Promise.resolve(this.status)
              .then(() => {
                const result = this.$validator.validate(
                  schema,
                  formData,
                  options,
                );

                for (const formGroup of formGroups) {
                  if (undefined === formGroup.dataset.name) {
                    continue;
                  }

                  const fieldName = formGroup.dataset.name;

                  if (result.has(fieldName)) {
                    const { error } = result.get(fieldName);

                    this.removeValidationError(fieldName);

                    if (undefined !== error) {
                      this.setValidationError(fieldName, error, options);
                    }
                  }

                  if (formGroup.contains(target)) {
                    break;
                  }
                }
              })
              .finally(() => {
                this.status = prevStatus;
              });
          },

          /**
           * Marks a field as invalid and displays its error message in the
           * associated feedback element. Sets `aria-invalid`, `aria-describedby`,
           * and native `setCustomValidity` on each control in the group.
           * @param {string} fieldName - The field's `data-name` value.
           * @param {string} message - The validation error message to display.
           */
          setValidationError(fieldName, message) {
            const formGroup = this.$el.querySelector(
              `${FORM_CONTROL_GROUP_CLASS}[data-name="${fieldName}"]`,
            );

            const fieldId = formGroup.dataset.id;

            this.setError({
              fieldName,
              message: message,
            });

            formGroup
              .querySelectorAll(FORM_CONTROL_CLASS)
              .forEach((control) => {
                control.setAttribute("aria-invalid", "true");
                control.classList.add("invalid");

                const feedbackId = `${fieldId}_Feedback`;
                const feedback = window[feedbackId];

                if (feedback && feedback instanceof HTMLElement) {
                  control.setAttribute("aria-describedby", feedbackId);
                  feedback.innerText = message;
                }

                if (typeof control.setCustomValidity === "function") {
                  control.setCustomValidity(message);
                }
              });
          },

          /**
           * Clears the invalid state and feedback message for the given field.
           * @param {string} fieldName - The field's `data-name` value.
           */
          removeValidationError(fieldName) {
            const formGroup = this.$el.querySelector(
              `${FORM_CONTROL_GROUP_CLASS}[data-name="${fieldName}"]`,
            );

            const fieldId = formGroup.dataset.id;

            formGroup.querySelectorAll("[aria-invalid]").forEach((elm) => {
              elm.setAttribute("aria-invalid", "false");
            });

            this.removeError(fieldName);

            formGroup
              .querySelectorAll(FORM_CONTROL_CLASS)
              .forEach((control) => {
                control.classList.remove("invalid");

                const feedbackId = `${fieldId}_Feedback`;
                const feedback = window[feedbackId];

                if (feedback && feedback instanceof HTMLElement) {
                  feedback.textContent = "";
                }

                if (typeof control.setCustomValidity === "function") {
                  control.setCustomValidity("");
                }
              });
          },

          /**
           * Removes the error entry for the given field name from the errors array.
           * @param {string} fieldName - The field name to remove.
           */
          removeError(fieldName) {
            this.__errors = this.__errors.filter((error) => {
              return fieldName !== error.field;
            });
          },

          /**
           * Adds an error entry to the errors array.
           * @param {{ fieldName: string, message: string }} param0 - The error to add.
           */
          setError({ fieldName, message }) {
            this.__errors.push({
              field: fieldName,
              message,
            });
          },

          /**
           * Removes all entries from the errors array.
           */
          clearErrors() {
            this.__errors.length = 0;
          },

          /**
           * Focuses the first field with `aria-invalid="true"` and announces
           * the error count to screen readers.
           */
          focusFirstInvalidField() {
            const firstInvalid = this.$el.querySelector(
              '[aria-invalid="true"]',
            );
            if (firstInvalid) {
              firstInvalid.focus();
              // Announce error count to screen readers.
              this.announceToScreenReader(
                `Form has ${this.__errors.length} error${this.__errors.length === 1 ? "" : "s"}. Please correct the highlighted fields.`,
              );
            }
          },

          /**
           * Writes a message to an `aria-live="polite"` region inside the form,
           * creating one if it does not already exist. Uses a requestAnimationFrame
           * to ensure the DOM update triggers an announcement.
           * @param {string} message - The message to announce.
           */
          announceToScreenReader(message) {
            let liveRegion = this.$el.querySelector('[aria-live="polite"]');
            if (!liveRegion) {
              liveRegion = document.createElement("div");
              liveRegion.setAttribute("aria-live", "polite");
              liveRegion.setAttribute("aria-atomic", "true");
              liveRegion.className = "sr-only";
              this.$el.prepend(liveRegion);
            }
            // Clear and set to trigger announcement.
            liveRegion.textContent = "";
            requestAnimationFrame(() => {
              liveRegion.textContent = message;
            });
          },

          /**
           * Collects values from all enabled form controls (excluding those inside
           * `.novalidate` groups) into a FormData object. Handles checkboxes,
           * radios, multi-selects, and file inputs appropriately.
           * @returns {FormData} The collected form data.
           */
          getFormData() {
            const formGroups = this.$el.querySelectorAll(
              FORM_CONTROL_GROUP_CLASS,
            );

            return Array.from(formGroups).reduce((formData, formGroup) => {
              if (formGroup.closest(NOVALIDATE_CLASS)) {
                return formData;
              }

              formGroup
                .querySelectorAll(":where( input, textarea, select ):enabled")
                .forEach((control) => {
                  if (!control.name) {
                    return;
                  }

                  switch (control.type) {
                    case "button":
                    case "image":
                    case "reset":
                    case "submit":
                      break;
                    case "checkbox":
                    case "radio":
                      if (control.checked) {
                        formData.append(control.name, control.value);
                      }
                      break;
                    case "select-multiple":
                      for (const option of control.selectedOptions) {
                        formData.append(control.name, option.value);
                      }
                      break;
                    case "file":
                      for (const file of control.files) {
                        formData.append(control.name, file);
                      }
                      break;
                    default:
                      formData.append(control.name, control.value);
                  }
                });

              return formData;
            }, new FormData());
          },

          /**
           * Returns true when there are one or more validation errors.
           * @returns {boolean}
           */
          get hasErrors() {
            return this.__errors.length > 0;
          },

          /**
           * Returns the current form status string.
           * @returns {string}
           */
          get status() {
            return this.__status;
          },

          /**
           * Sets the form status. Known status keys are mapped to their
           * canonical values; unknown values are sanitised and prefixed with
           * `custom-`.
           * @param {string} value - The status key to set.
           */
          set status(value) {
            let newStatus = value;

            if (DEFAULT_STATUSES.has(value)) {
              newStatus = DEFAULT_STATUSES.get(value);
            }

            if (!Array.from(DEFAULT_STATUSES.values()).includes(newStatus)) {
              newStatus = newStatus.replace(/[^0-9a-z]+/gi, " ").trim();
              newStatus = newStatus.replace(/\s+/g, "-");
              newStatus = `custom-${newStatus}`;
            }

            this.__status = newStatus;
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("form", (el, { expression }, { evaluate }) => {
      root(Alpine, el, expression ? evaluate(expression) : {});
    }).before("bind");
  });
})();
